'use client';

import { useState } from 'react';

import { getFilteredRowModel, getSortedRowModel } from '@tanstack/react-table';

import type { FilterFn, SortingState, TableOptions, Updater } from '@tanstack/react-table';

/**
 * Opts a table into browser-side sorting and searching over the rows it already holds.
 */
export interface ClientDataConfig<TRow> {
  /** Matcher for the global filter box. */
  globalFilterFn?: FilterFn<TRow>;
}

/** `ClientDataConfig` plus the initial sort state the table supplies. */
export interface ResolvedClientDataConfig<TRow> extends ClientDataConfig<TRow> {
  initialSorting?: SortingState;
}

type ClientTableOptions<TRow> = Pick<
  TableOptions<TRow>,
  | 'getFilteredRowModel'
  | 'getSortedRowModel'
  | 'globalFilterFn'
  | 'manualFiltering'
  | 'manualSorting'
  | 'onGlobalFilterChange'
  | 'onSortingChange'
>;

export interface UseClientTableModelResult<TRow> {
  /** Whether client-side sorting and filtering are enabled. */
  enabled: boolean;
  /** Current global filter value (controlled). */
  globalFilter: string;
  /** Sets the global filter value. */
  setGlobalFilter: (value: string) => void;
  /** Current client sort state (controlled). */
  sorting: SortingState;
  /** Sets the client sort state. */
  setSorting: (updater: Updater<SortingState>) => void;
  /**
   * TanStack options to spread into `useReactTable`: everything except `state`,
   * which the caller merges with any other controlled state (e.g. row selection).
   * Empty when client-side data handling is disabled.
   */
  tableOptions: ClientTableOptions<TRow>;
}

/**
 * Owns the state that turns a table into a client-side one: the sort state
 * behind clickable column headers and the value behind a search box, plus the
 * row models and `manual*: false` flags that make TanStack do the work in the
 * browser. Callers merge `sorting` and `globalFilter` into their own `state`
 * slice so both can coexist with other controlled state.
 */
export function useClientTableModel<TRow>(
  config: ResolvedClientDataConfig<TRow> | undefined,
): UseClientTableModelResult<TRow> {
  const [sorting, setSorting] = useState<SortingState>(config?.initialSorting ?? []);
  const [globalFilter, setGlobalFilter] = useState('');

  const tableOptions: ClientTableOptions<TRow> = config
    ? {
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        ...(config.globalFilterFn && { globalFilterFn: config.globalFilterFn }),
        manualFiltering: false,
        manualSorting: false,
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
      }
    : {};

  return {
    enabled: !!config,
    globalFilter,
    setGlobalFilter,
    sorting,
    setSorting,
    tableOptions,
  };
}
